import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Sparkles, Eye, Layers, Loader2, Search, ArrowLeft, Bell, X, MapPin, Phone, Mail, Globe, Instagram, Facebook, Linkedin, MessageCircle, BadgeCheck, Star, Upload, Camera, ScanSearch, Image as ImageIcon, Shirt, Play, Plus, Equal } from 'lucide-react';
import FabricCatalogueView from './FabricCatalogueView';
import { getPublicCollectionById, getPublicCollectionsByPhone } from '../api/fabricCollection.api';
import { getPublicProductsByPhone, semanticSearchPublicFabrics, visualSearchPublicFabrics } from '../api/product.api';
import { getPublicKitsByPhone } from '../api/kit.api';
import { getPublicInspirationalDesignsByPhone } from '../api/inspirationalDesign.api';
import { subscribeToCollection, getUserProfile, updateUserProfile, API_BASE_URL } from '../api';
import { toThumbnailUrl } from '../utils/imageThumbnailUrl';
import { getVendorThemeCssVars } from '../utils/publicVendorThemes';
import { uploadImageToS3, compressImage } from '../utils/s3Upload';

/** Group semantic/visual product hits into collection-shaped cards for this seller's page */
function productsToGroupedCollections(products) {
  const map = new Map();
  for (const p of products || []) {
    const fc = p.fabricCollection;
    if (!fc) continue;
    const cid = String(fc._id || fc);
    if (!map.has(cid)) {
      map.set(cid, {
        _id: cid,
        name: typeof fc === 'object' && fc.name ? fc.name : 'Collection',
        image: typeof fc === 'object' && fc.image ? fc.image : null,
        tags: [],
        properties: [],
        fabrics: []
      });
    }
    const coll = map.get(cid);
    if (!coll.fabrics.some((f) => String(f._id) === String(p._id))) {
      coll.fabrics.push(p);
    }
  }
  return Array.from(map.values());
}

function mergeGroupedCollections(prevCollections, newProducts) {
  const map = new Map(
    (prevCollections || []).map((c) => [String(c._id), { ...c, fabrics: [...(c.fabrics || [])] }])
  );
  for (const p of newProducts || []) {
    const fc = p.fabricCollection;
    if (!fc) continue;
    const cid = String(fc._id || fc);
    if (!map.has(cid)) {
      map.set(cid, {
        _id: cid,
        name: typeof fc === 'object' && fc.name ? fc.name : 'Collection',
        image: typeof fc === 'object' && fc.image ? fc.image : null,
        tags: [],
        properties: [],
        fabrics: []
      });
    }
    const coll = map.get(cid);
    if (!coll.fabrics.some((f) => String(f._id) === String(p._id))) {
      coll.fabrics.push(p);
    }
  }
  return Array.from(map.values());
}

function mergeVisualSearchScores(products, visualScores, isVisualSearch) {
  const list = Array.isArray(products) ? products : [];
  if (!isVisualSearch || !Array.isArray(visualScores)) {
    return list.map(({ visualMatchScore: _drop, ...rest }) => rest);
  }
  return list.map((p, i) => {
    const { visualMatchScore: _prev, ...base } = p;
    const raw = visualScores[i];
    if (raw == null) return base;
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n)) return base;
    return { ...base, visualMatchScore: n };
  });
}

// Lazy Image Component with IntersectionObserver (thumbnail first, original on error — matches My Fabrics)
const LazyImage = ({ src, alt, className, onError, fallbackSrc, ...props }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px',
        threshold: 0.01
      }
    );

    observer.observe(img);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    setUseFallback(false);
    setHasError(false);
    setIsLoaded(false);
  }, [src, fallbackSrc]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = (e) => {
    if (!useFallback && fallbackSrc && fallbackSrc !== src) {
      setUseFallback(true);
      setIsLoaded(false);
      return;
    }
    setHasError(true);
    setIsLoaded(true);
    if (onError) {
      onError(e);
    }
  };

  const activeSrc = useFallback ? fallbackSrc : src;

  return (
    <div ref={imgRef} className="relative w-full h-full">
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center z-0">
          <Loader2 size={24} className="text-gray-400 animate-spin" />
        </div>
      )}

      {isInView && (
        <img
          src={activeSrc}
          alt={alt}
          className={`${className} transition-opacity duration-300 relative z-10 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
          {...props}
        />
      )}

      {hasError && isInView && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-20">
          <div className="text-center p-2">
            <Layers size={24} className="text-gray-400 mx-auto mb-1" />
            <p className="text-xs text-gray-500">Image unavailable</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Public Fabric Detail View Component - Uses the same FabricCatalogueView as MyProductsView

// Public Fabric Collection Detail View Component
const PublicFabricCollectionDetailView = ({
  collection,
  onBack,
  onViewCatalogue,
  onGenerateWithModel,
  showNotification,
  onOpenLoginModal,
  onOpenChildCollection,
}) => {
  const [fabricSearchTerm, setFabricSearchTerm] = useState('');
  const [selectedFabric, setSelectedFabric] = useState(null);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const isHandlingPopState = useRef(false);
  const alertMessage = (message) => {
    window.alert(message);
  };

  const getFabricImage = (fabric) => {
    return (
      fabric.image ||
      fabric.images?.original?.s3Url ||
      fabric.images?.original?.imgbbUrl ||
      fabric.images?.original?.url ||
      'https://via.placeholder.com/300x300?text=No+Image'
    );
  };

  const getCollectionImage = () => {
    const coverImage = collection?.image?.url || collection?.image || null;
    if (coverImage) return coverImage;
    return (collection?.fabrics || []).map(getFabricImage).find(Boolean) || null;
  };

  // Filter fabrics by search term
  const filteredFabrics = collection?.fabrics?.filter(fabric => {
    if (!fabricSearchTerm.trim()) return true;
    const searchLower = fabricSearchTerm.toLowerCase();
    return (
      fabric.name?.toLowerCase().includes(searchLower) ||
      fabric.description?.toLowerCase().includes(searchLower) ||
      fabric.category?.toLowerCase().includes(searchLower)
    );
  }) || [];

  // Public Fabric Card Component
  const PublicFabricCard = ({ fabric }) => {
    const originalUrl = getFabricImage(fabric);
    const thumbnailUrl = toThumbnailUrl(originalUrl);
    const variantCount = fabric.colorVariants ? fabric.colorVariants.length : 0;

    return (
      <div className="group bg-white rounded-lg sm:rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 overflow-hidden">
        <div 
          className="relative aspect-square bg-gray-100 cursor-pointer overflow-hidden"
          onClick={() => handleOpenFabric(fabric)}
          title="View fabric details"
        >
          <LazyImage
            src={thumbnailUrl}
            fallbackSrc={originalUrl}
            alt={fabric.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          {variantCount > 0 && (
            <div className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-blue-500 text-white text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full font-medium">
              +{variantCount}
            </div>
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 opacity-0 group-hover:opacity-100 transition-all duration-300">
            <div className="bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg transform scale-90 group-hover:scale-100 transition-transform">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                <Eye size={16} />
                <span>View Details</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-3 sm:p-4">
          <h3 className="font-semibold text-gray-800 mb-1 line-clamp-1 text-sm sm:text-base">{fabric.name}</h3>
          {Number.isFinite(fabric.visualMatchScore) && (
            <div
              className="mb-2 sm:mb-3 flex items-center gap-1.5 rounded-lg bg-cyan-50 border border-cyan-100 px-2 py-1.5 text-xs font-semibold text-cyan-900"
              title="Visual similarity (cosine)"
            >
              <ScanSearch size={14} className="text-cyan-600 shrink-0" aria-hidden />
              <span className="tabular-nums">{(fabric.visualMatchScore * 100).toFixed(1)}% visual match</span>
            </div>
          )}
          <p className="text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3 line-clamp-2">{fabric.description}</p>
          
          <div className="flex gap-1.5 sm:gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onGenerateWithModel(fabric);
              }}
              className="flex-1 bg-blue-600 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-md sm:rounded-lg text-xs sm:text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
              title="Generate kit with this fabric"
            >
              <Sparkles size={12} className="sm:w-3.5 sm:h-3.5" />
              <span>Generate</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleOpenFabric(fabric);
              }}
              className="px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-100 text-gray-600 rounded-md sm:rounded-lg hover:bg-gray-200 transition-colors"
              title="View details"
            >
              <Eye size={12} className="sm:w-3.5 sm:h-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Handle opening fabric detail and push history state
  const handleOpenFabric = useCallback((fabric) => {
    setSelectedFabric(fabric);
    // Push state to browser history
    window.history.pushState(
      { view: 'public-fabric-detail', fabricId: fabric._id },
      '',
      window.location.pathname
    );
  }, []);

  // Handle closing fabric detail
  const handleCloseFabric = useCallback(() => {
    if (isHandlingPopState.current) {
      isHandlingPopState.current = false;
      return;
    }
    setSelectedFabric(null);
    // Only go back if there's history
    if (window.history.state?.view === 'public-fabric-detail') {
      window.history.back();
    }
  }, []);

  // Listen for browser back button
  useEffect(() => {
    const handlePopState = (event) => {
      if (selectedFabric) {
        isHandlingPopState.current = true;
        setSelectedFabric(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [selectedFabric]);

  // Show fabric detail view - Use the same FabricCatalogueView as MyProductsView
  if (selectedFabric) {
    return (
      <FabricCatalogueView
        fabric={selectedFabric}
        onClose={handleCloseFabric}
        showNotification={showNotification}
        allowShadeCardUpload={false}
        onGenerateFabric={onGenerateWithModel}
      />
    );
  }

  const collectionImage = getCollectionImage();
  const propertyEntries = (collection?.properties || []).map((prop) => {
    if (prop.key && prop.value) return `${prop.key}: ${prop.value}`;
    return prop.key || prop.value || '';
  }).filter(Boolean);

  const handleSubscribe = async () => {
    // Get phone number from collection owner
    const collectionOwnerPhone = collection?.user?.phone || collection?.phoneNumber || null;
    
    if (!collectionOwnerPhone) {
      alertMessage('Unable to find collection owner information');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      // User is not logged in - store subscription info and open login modal
      const pendingSubscription = {
        phoneNumber: collectionOwnerPhone.replace(/\D/g, ''), // Normalize phone number
        source: 'public-fabric-collection',
        collectionId: collection?._id
      };
      localStorage.setItem('pendingSubscription', JSON.stringify(pendingSubscription));
      console.log('?? Stored pending subscription:', pendingSubscription);
      
      // Open login modal
      if (onOpenLoginModal && typeof onOpenLoginModal === 'function') {
        onOpenLoginModal();
      } else {
        alertMessage('Please login to subscribe. Your subscription will be completed after login.');
      }
      return;
    }

    // User is logged in - directly subscribe
    setIsSubscribing(true);
    try {
      await subscribeToCollection(collectionOwnerPhone.replace(/\D/g, ''), token);
      
      alertMessage('Successfully subscribed! You will receive updates on new collections.');
      window.location.href = '/';
    } catch (error) {
      console.error('Subscription error:', error);
      if (error.response?.status === 401) {
        // Token expired - store pending subscription and open login modal
        const pendingSubscription = {
          phoneNumber: collectionOwnerPhone.replace(/\D/g, ''),
          source: 'public-fabric-collection',
          collectionId: collection?._id
        };
        localStorage.setItem('pendingSubscription', JSON.stringify(pendingSubscription));
        
        if (onOpenLoginModal && typeof onOpenLoginModal === 'function') {
          onOpenLoginModal();
        }
      }

      const backendMessage = error.response?.data?.message || error.message;
      if (backendMessage?.toLowerCase().includes('already subscribed')) {
        alertMessage('Seller already subscribed.');
      } else {
        alertMessage(backendMessage || 'Failed to subscribe. Please try again later.');
      }
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[var(--pv-page-from)] via-[var(--pv-page-via)] to-[var(--pv-page-to)]">
      {/* Header */}
      <div
        className="text-white px-4 sm:px-6 py-4 shadow-lg"
        style={{
          background: 'linear-gradient(to right, var(--pv-hero-from), var(--pv-hero-to))',
        }}
      >
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-white/15 rounded-lg transition-colors"
            title="Go back"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-bold">{collection?.name || 'Fabric Collection'}</h1>
            <p className="text-sm text-white/80 mt-1">
              {filteredFabrics.length} {filteredFabrics.length === 1 ? 'fabric' : 'fabrics'} in this collection
            </p>
          </div>
          <button
            onClick={handleSubscribe}
            disabled={isSubscribing}
            className="px-3 py-1.5 text-xs sm:text-sm bg-[var(--pv-badge-secondary)] text-white rounded-lg hover:opacity-90 active:scale-95 transition flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Subscribe"
          >
            {isSubscribing ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                <span className="hidden sm:inline">Subscribing...</span>
              </>
            ) : (
              <>
                <Bell size={14} />
                <span className="hidden sm:inline">Subscribe</span>
              </>
            )}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Child Collections */}
        {Array.isArray(collection?.childCollections) && collection.childCollections.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg border border-[var(--pv-card-border)] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Sub-collections</h3>
              <span className="text-xs text-gray-500">{collection.childCollections.length}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
              {collection.childCollections.map((child) => {
                const img = child?.image?.url || null;
                const fabricCount = Array.isArray(child?.fabrics) ? child.fabrics.length : 0;
                return (
                  <button
                    type="button"
                    key={child._id}
                    onClick={() => onOpenChildCollection?.(child)}
                    className="group text-left bg-white rounded-lg sm:rounded-xl border border-gray-200 hover:border-[var(--pv-primary)] hover:shadow-lg transition-all duration-300 overflow-hidden"
                    title="Open collection"
                  >
                    <div className="relative aspect-square bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50 overflow-hidden">
                      {img ? (
                        <img
                          src={img}
                          alt={child.name || 'Collection'}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-purple-300">
                          <Layers size={26} />
                        </div>
                      )}
                      <div className="absolute top-2 left-2 text-[10px] sm:text-xs font-semibold px-2 py-0.5 rounded-full bg-[var(--pv-primary)] text-white shadow">
                        Collection
                      </div>
                      <div className="absolute top-2 right-2 text-[10px] sm:text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-500 text-white shadow">
                        {fabricCount} {fabricCount === 1 ? 'fabric' : 'fabrics'}
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="font-semibold text-gray-800 line-clamp-1 text-sm">{child.name || 'Collection'}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Collection Info */}
        <div className="bg-white rounded-2xl shadow-lg border border-[var(--pv-card-border)] p-6">
          <div className="flex flex-col sm:flex-row gap-6">
            {collectionImage && (
              <div className="w-full sm:w-48 h-48 rounded-xl overflow-hidden flex-shrink-0">
                <img
                  src={collectionImage}
                  alt={collection?.name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{collection?.name}</h2>
              
              {propertyEntries.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Properties</h3>
                  <div className="flex flex-wrap gap-2">
                    {propertyEntries.map((entry, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 rounded-full text-xs font-medium bg-[var(--pv-tag-bg)] text-[var(--pv-tag-text)]"
                      >
                        {entry}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {collection?.tags && collection.tags.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {collection.tags.map(tag => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-lg border border-[var(--pv-card-border)] p-4">
          <div className="relative max-w-md">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search fabrics by name, description..."
              value={fabricSearchTerm}
              onChange={(e) => setFabricSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--pv-primary)] focus:border-transparent text-sm"
            />
          </div>
        </div>

        {/* Fabrics Grid */}
        <div>
          {filteredFabrics.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg border border-[var(--pv-card-border)] p-12 text-center">
              <Layers size={48} className="text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">
                {fabricSearchTerm ? 'No fabrics found matching your search.' : 'No fabrics in this collection.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
              {filteredFabrics.map((fabric) => (
                <PublicFabricCard key={fabric._id} fabric={fabric} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PublicFabricView = ({
  vendorSlug,
  phoneNumber,
  setActiveView,
  setProductDetails,
  setPreSelectedProduct,
  showNotification,
  onOpenLoginModal,
  defaultAdminView = false,
  containerClassName = 'max-w-7xl',
  containerPaddingClassName = 'px-3 sm:px-4 lg:px-8'
}) => {
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedLooseFabric, setSelectedLooseFabric] = useState(null);
  const [catalogueFabric, setCatalogueFabric] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [visualSearchImageUrl, setVisualSearchImageUrl] = useState('');
  const [visualSearchImagePreview, setVisualSearchImagePreview] = useState('');
  const [isVisualSearchUploading, setIsVisualSearchUploading] = useState(false);
  const [showImageSearchOptions, setShowImageSearchOptions] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  /** Sub-tabs when main tab is Collections */
  const [collectionsSubTab, setCollectionsSubTab] = useState('fabrics');
  const [inspirationDesigns, setInspirationDesigns] = useState([]);
  const [inspirationPage, setInspirationPage] = useState(1);
  const [inspirationHasNextPage, setInspirationHasNextPage] = useState(false);
  const [inspirationLoading, setInspirationLoading] = useState(false);
  const [inspirationLoadingMore, setInspirationLoadingMore] = useState(false);
  const [selectedInspirationDesign, setSelectedInspirationDesign] = useState(null);
  const [publicKits, setPublicKits] = useState([]);
  const [kitPage, setKitPage] = useState(1);
  const [kitHasNextPage, setKitHasNextPage] = useState(false);
  const [kitLoading, setKitLoading] = useState(false);
  const [kitLoadingMore, setKitLoadingMore] = useState(false);
  const [selectedPublicKit, setSelectedPublicKit] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [loosePublicFabrics, setLoosePublicFabrics] = useState([]);
  const [looseFabricsLoading, setLooseFabricsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [isAdminView, setIsAdminView] = useState(defaultAdminView);
  const [editingSection, setEditingSection] = useState(null);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isUploadingBusinessPhotos, setIsUploadingBusinessPhotos] = useState(false);
  const [publicProfileDraft, setPublicProfileDraft] = useState({
    brandName: '',
    tagline: '',
    bio: '',
    location: '',
    address: '',
    since: '',
    website: '',
    contactEmail: '',
    avatarUrl: '',
    coverUrl: '',
    specialties: '',
    services: '',
    businessPhotos: '',
    googleReviews: '',
    instagram: '',
    facebook: '',
    linkedin: '',
    whatsapp: ''
  });
  const [error, setError] = useState(null);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const observerTarget = useRef(null);
  const searchTimeoutRef = useRef(null);
  const visualSearchInputRef = useRef(null);
  const visualSearchCameraInputRef = useRef(null);
  const imageSearchMenuRef = useRef(null);
  const avatarInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const businessPhotosInputRef = useRef(null);
  const alertMessage = (message) => window.alert(message);

  const vendorIdentifier = (vendorSlug || phoneNumber || '').trim();
  const resolvedPhoneDigits = userInfo?.phone
    ? String(userInfo.phone).replace(/\D/g, '')
    : (/^\d{10,}$/.test(vendorIdentifier.replace(/\D/g, '')) ? vendorIdentifier.replace(/\D/g, '') : '');

  const sellerSearchFilters = useMemo(
    () => ({
      phoneNumber: resolvedPhoneDigits || vendorIdentifier,
      hasCollection: 'true'
    }),
    [resolvedPhoneDigits, vendorIdentifier]
  );

  const formatPhone = (digits) => {
    if (!digits) return '';
    const cleaned = digits.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    }
    if (cleaned.length > 10) {
      return `+${cleaned}`;
    }
    return cleaned;
  };

  const fallbackSeller = {
    name: 'Your Brand Name',
    brandName: 'Your Fabric Studio',
    tagline: 'Add a short tagline about your brand.',
    bio: 'Write a short introduction about your fabrics, services, and what makes you unique.',
    location: 'City, State',
    address: 'Street, Area, City',
    since: 'YYYY',
    email: 'your-email@example.com',
    phone: formatPhone(resolvedPhoneDigits) || '+00 00000 00000',
    website: 'www.yourwebsite.com',
    specialties: ['Specialty 1', 'Specialty 2', 'Specialty 3', 'Specialty 4'],
    services: ['Service 1', 'Service 2', 'Service 3', 'Service 4'],
    businessPhotos: [
      'https://placehold.co/900x600/1d4ed8/ffffff?text=Upload+Photo+1',
      'https://placehold.co/900x600/7c3aed/ffffff?text=Upload+Photo+2',
      'https://placehold.co/900x600/f97316/ffffff?text=Upload+Photo+3'
    ],
    googleReviews: [
      { author: 'Customer Name', rating: 5, text: 'Add a short review from your customer.' },
      { author: 'Customer Name', rating: 4.5, text: 'Add another review here.' },
      { author: 'Customer Name', rating: 4.8, text: 'Add another review here.' }
    ],
    rating: 4.9,
    reviews: 268
  };

  const publicProfile = userInfo?.publicProfile || {};

  const vendorCssVars = useMemo(
    () => getVendorThemeCssVars(publicProfile.publicSiteTheme),
    [publicProfile.publicSiteTheme]
  );

  const profileReviews = publicProfile.googleReviews?.length
    ? publicProfile.googleReviews
    : (isAdminView ? fallbackSeller.googleReviews : []);
  const reviewRatings = profileReviews
    .map((review) => Number(review?.rating))
    .filter((rating) => Number.isFinite(rating) && rating > 0);
  const computedReviewCount = profileReviews.length;
  const computedRating = reviewRatings.length
    ? Math.round((reviewRatings.reduce((sum, rating) => sum + rating, 0) / reviewRatings.length) * 10) / 10
    : null;

  // Only use fallback values in admin view, otherwise show actual data or nothing
  const displaySeller = {
    name: userInfo?.name || (isAdminView ? fallbackSeller.name : ''),
    brandName: publicProfile.brandName || (isAdminView ? fallbackSeller.brandName : ''),
    tagline: publicProfile.tagline || (isAdminView ? fallbackSeller.tagline : ''),
    bio: publicProfile.bio || (isAdminView ? fallbackSeller.bio : ''),
    location: publicProfile.location || (isAdminView ? fallbackSeller.location : ''),
    address: publicProfile.address || (isAdminView ? fallbackSeller.address : ''),
    since: publicProfile.since || (isAdminView ? fallbackSeller.since : ''),
    email: publicProfile.contactEmail || userInfo?.email || (isAdminView ? fallbackSeller.email : ''),
    phone: formatPhone(userInfo?.phone || resolvedPhoneDigits) || (isAdminView ? fallbackSeller.phone : ''),
    website: publicProfile.website || (isAdminView ? fallbackSeller.website : ''),
    avatarUrl: publicProfile.avatarUrl || '',
    coverUrl: publicProfile.coverUrl || '',
    specialties: publicProfile.specialties?.length ? publicProfile.specialties : (isAdminView ? fallbackSeller.specialties : []),
    services: publicProfile.services?.length ? publicProfile.services : (isAdminView ? fallbackSeller.services : []),
    businessPhotos: publicProfile.businessPhotos?.length ? publicProfile.businessPhotos : (isAdminView ? fallbackSeller.businessPhotos : []),
    googleReviews: profileReviews,
    rating: computedRating ?? (isAdminView ? fallbackSeller.rating : null),
    reviews: computedReviewCount || (isAdminView ? fallbackSeller.reviews : 0),
    socials: {
      instagram: publicProfile.socials?.instagram || (isAdminView ? 'https://instagram.com/aarohifabrics' : ''),
      facebook: publicProfile.socials?.facebook || (isAdminView ? 'https://facebook.com/aarohifabrics' : ''),
      linkedin: publicProfile.socials?.linkedin || (isAdminView ? 'https://linkedin.com/company/aarohi-fabrics' : ''),
      whatsapp: publicProfile.socials?.whatsapp || (resolvedPhoneDigits ? `https://wa.me/${resolvedPhoneDigits}` : '')
    }
  };

  const renderStars = (rating, size = 14) => {
    const value = Number(rating) || 0;
    return (
      <span className="inline-flex items-center gap-0.5" aria-label={`${value} out of 5 stars`}>
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={size}
            className={star <= Math.round(value) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
          />
        ))}
      </span>
    );
  };

  const stats = [
    { label: 'Collections', value: collections.length },
    { label: 'Fabrics', value: collections.reduce((sum, c) => sum + (c.fabrics?.length || 0), 0) },
    { label: 'Years', value: Math.max(3, (new Date().getFullYear() - Number(displaySeller.since)) || 12) }
  ];

  const toList = (value) =>
    value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);

  const hydrateDraftFromProfile = (profile) => {
    const reviewsText = (profile?.googleReviews || [])
      .map((review) => {
        const author = review.author || '';
        const rating = review.rating ?? '';
        const text = review.text || '';
        return `${author} | ${rating} | ${text}`.trim();
      })
      .filter(Boolean)
      .join('\n');

    setPublicProfileDraft({
      brandName: profile?.brandName || '',
      tagline: profile?.tagline || '',
      bio: profile?.bio || '',
      location: profile?.location || '',
      address: profile?.address || '',
      since: profile?.since || '',
      website: profile?.website || '',
      contactEmail: profile?.contactEmail || '',
      avatarUrl: profile?.avatarUrl || '',
      coverUrl: profile?.coverUrl || '',
      specialties: (profile?.specialties || []).join(', '),
      services: (profile?.services || []).join(', '),
      businessPhotos: (profile?.businessPhotos || []).join(', '),
      googleReviews: reviewsText,
      instagram: profile?.socials?.instagram || '',
      facebook: profile?.socials?.facebook || '',
      linkedin: profile?.socials?.linkedin || '',
      whatsapp: profile?.socials?.whatsapp || ''
    });
  };

  const handleSavePublicProfile = async () => {
    try {
      setIsSavingProfile(true);
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification?.('Please login to update your public page.', 'error');
        return;
      }

      const parseReviews = (text) =>
        text
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => {
            const [author = '', rating = '', reviewText = ''] = line.split('|').map(part => part.trim());
            return {
              author,
              rating: rating ? Number(rating) : undefined,
              text: reviewText,
              source: 'Google'
            };
          })
          .filter((review) => review.author || review.text);

      const payload = {
        brandName: publicProfileDraft.brandName,
        tagline: publicProfileDraft.tagline,
        bio: publicProfileDraft.bio,
        location: publicProfileDraft.location,
        address: publicProfileDraft.address,
        since: publicProfileDraft.since,
        website: publicProfileDraft.website,
        contactEmail: publicProfileDraft.contactEmail,
        avatarUrl: publicProfileDraft.avatarUrl,
        coverUrl: publicProfileDraft.coverUrl,
        specialties: toList(publicProfileDraft.specialties),
        services: toList(publicProfileDraft.services),
        businessPhotos: toList(publicProfileDraft.businessPhotos),
        googleReviews: parseReviews(publicProfileDraft.googleReviews),
        socials: {
          instagram: publicProfileDraft.instagram,
          facebook: publicProfileDraft.facebook,
          linkedin: publicProfileDraft.linkedin,
          whatsapp: publicProfileDraft.whatsapp
        }
      };

      const response = await updateUserProfile({ publicProfile: payload }, token);
      const updatedProfile = response?.data?.data?.user?.publicProfile || payload;
      setUserInfo(prev => prev ? ({ ...prev, publicProfile: updatedProfile }) : prev);
      hydrateDraftFromProfile(updatedProfile);
      setEditingSection(null);
      showNotification?.('Public page updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating public page profile:', error);
      showNotification?.('Failed to update public page. Please try again.', 'error');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleImageUpload = async (type, file) => {
    if (!file) return;
    const token = localStorage.getItem('token');
    if (!token) {
      showNotification?.('Please login to upload images.', 'error');
      return;
    }

    const isAvatar = type === 'avatar';
    try {
      if (isAvatar) {
        setIsUploadingAvatar(true);
      } else {
        setIsUploadingCover(true);
      }

      const formData = new FormData();
      formData.append('image', file);
      formData.append('folder', isAvatar ? 'public-profiles/avatars' : 'public-profiles/covers');

      const response = await fetch(`${API_BASE_URL}/s3/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Upload failed');
      }

      const data = await response.json();
      const uploadedUrl = data?.url;
      if (!uploadedUrl) {
        throw new Error('Upload succeeded but URL not returned');
      }

      const updatePayload = isAvatar ? { avatarUrl: uploadedUrl } : { coverUrl: uploadedUrl };
      const profileRes = await updateUserProfile({ publicProfile: updatePayload }, token);
      const updatedProfile = profileRes?.data?.data?.user?.publicProfile;

      setPublicProfileDraft(prev => ({ ...prev, ...updatePayload }));
      setUserInfo(prev => prev ? ({
        ...prev,
        publicProfile: { ...(prev.publicProfile || {}), ...(updatedProfile || updatePayload) }
      }) : prev);

      showNotification?.('Image uploaded successfully!', 'success');
    } catch (error) {
      console.error('Image upload error:', error);
      showNotification?.(error.message || 'Failed to upload image.', 'error');
    } finally {
      if (isAvatar) {
        setIsUploadingAvatar(false);
        if (avatarInputRef.current) avatarInputRef.current.value = '';
      } else {
        setIsUploadingCover(false);
        if (coverInputRef.current) coverInputRef.current.value = '';
      }
    }
  };

  const handleBusinessPhotosUpload = async (files) => {
    const selectedFiles = Array.from(files || []);
    if (!selectedFiles.length) return;
    const token = localStorage.getItem('token');
    if (!token) {
      showNotification?.('Please login to upload images.', 'error');
      return;
    }

    try {
      setIsUploadingBusinessPhotos(true);
      const uploadedUrls = [];

      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('folder', 'public-profiles/business-photos');

        const response = await fetch(`${API_BASE_URL}/s3/upload`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Upload failed');
        }

        const data = await response.json();
        if (data?.url) {
          uploadedUrls.push(data.url);
        }
      }

      const currentPhotos = toList(publicProfileDraft.businessPhotos);
      const updatedPhotos = [...currentPhotos, ...uploadedUrls];

      const profileRes = await updateUserProfile({ publicProfile: { businessPhotos: updatedPhotos } }, token);
      const updatedProfile = profileRes?.data?.data?.user?.publicProfile;

      setPublicProfileDraft(prev => ({ ...prev, businessPhotos: updatedPhotos.join(', ') }));
      setUserInfo(prev => prev ? ({
        ...prev,
        publicProfile: { ...(prev.publicProfile || {}), ...(updatedProfile || { businessPhotos: updatedPhotos }) }
      }) : prev);

      showNotification?.('Business photos uploaded successfully!', 'success');
    } catch (error) {
      console.error('Business photos upload error:', error);
      showNotification?.(error.message || 'Failed to upload business photos.', 'error');
    } finally {
      setIsUploadingBusinessPhotos(false);
      if (businessPhotosInputRef.current) {
        businessPhotosInputRef.current.value = '';
      }
    }
  };

  const handleConnectGoogleBusiness = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showNotification?.('Please login to connect Google Business.', 'error');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/users/google-business/connect`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Google Business connection not configured');
      }

      const data = await response.json();
      if (data?.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      } else {
        throw new Error('Connection URL not available');
      }
    } catch (error) {
      console.error('Google Business connect error:', error);
      showNotification?.(error.message || 'Failed to connect Google Business.', 'error');
    }
  };

  const clearVisualSearch = useCallback(() => {
    if (visualSearchImagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(visualSearchImagePreview);
    }
    setVisualSearchImagePreview('');
    setVisualSearchImageUrl('');
    if (visualSearchInputRef.current) visualSearchInputRef.current.value = '';
    if (visualSearchCameraInputRef.current) visualSearchCameraInputRef.current.value = '';
  }, [visualSearchImagePreview]);

  const processVisualSearchFile = useCallback(
    async (file) => {
      if (!file) return;
      if (!file.type?.startsWith('image/')) {
        showNotification?.('Please select an image file', 'error');
        return;
      }
      setIsVisualSearchUploading(true);
      try {
        const compressedFile = await compressImage(file, { maxWidth: 1280, maxHeight: 1280, quality: 0.85 });
        const previewUrl = URL.createObjectURL(compressedFile);
        setVisualSearchImagePreview((prev) => {
          if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
          return previewUrl;
        });
        const uploadResult = await uploadImageToS3(compressedFile, {
          folder: 'visual-search',
          fileName: `public-page-visual-${Date.now()}-${compressedFile.name}`
        });
        const uploadedUrl = uploadResult?.url || '';
        if (!uploadedUrl) throw new Error('Failed to upload search image');
        setSearchInput('');
        setSearchQuery('');
        setVisualSearchImageUrl(uploadedUrl);
        showNotification?.('Image search enabled', 'success');
      } catch (error) {
        console.error('Error preparing visual search image:', error);
        showNotification?.(error?.message || 'Failed to process search image', 'error');
        clearVisualSearch();
      } finally {
        setIsVisualSearchUploading(false);
        if (visualSearchInputRef.current) visualSearchInputRef.current.value = '';
        if (visualSearchCameraInputRef.current) visualSearchCameraInputRef.current.value = '';
      }
    },
    [clearVisualSearch, showNotification]
  );

  const handleVisualSearchImageSelect = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      await processVisualSearchFile(file);
    },
    [processVisualSearchFile]
  );

  const handleVisualSearchCameraCapture = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      await processVisualSearchFile(file);
    },
    [processVisualSearchFile]
  );

  const openImageSearchOption = useCallback((mode) => {
    setShowImageSearchOptions(false);
    if (mode === 'camera') {
      visualSearchCameraInputRef.current?.click();
      return;
    }
    visualSearchInputRef.current?.click();
  }, []);

  // Fetch fabric collections with pagination (plain list) or semantic / visual search grouped by collection
  const fetchCollections = useCallback(
    async (page = 1, append = false, skipCache = false) => {
      if (!vendorIdentifier) return;

      const trimmed = (searchQuery || '').trim();
      const useSemantic = Boolean(trimmed || visualSearchImageUrl);

      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setError(null);
      }

      try {
        if (!useSemantic) {
          const response = await getPublicCollectionsByPhone(vendorIdentifier, page, 20, '', skipCache);
          const { collections: fetchedCollections, pagination, user } = response;

          if (append) {
            setCollections((prev) => [...prev, ...fetchedCollections]);
          } else {
            setCollections(fetchedCollections);
          }

          setUserInfo(user);
          setCurrentPage(pagination.currentPage);
          setHasNextPage(pagination.hasNextPage);
          return;
        }

        let response;
        if (visualSearchImageUrl) {
          response = await visualSearchPublicFabrics({
            imageUrl: visualSearchImageUrl,
            page,
            limit: 20,
            filters: sellerSearchFilters,
            candidateLimit: 250,
            maxNewEmbeddings: 10,
            strictMode: true
          });
        } else {
          response = await semanticSearchPublicFabrics({
            queryText: trimmed,
            page,
            limit: 20,
            filters: sellerSearchFilters,
            candidateLimit: 250,
            maxNewEmbeddings: 10
          });
        }

        const rawProducts = response.products || [];
        const mergedProducts = mergeVisualSearchScores(
          rawProducts,
          response.scores,
          Boolean(visualSearchImageUrl)
        );
        setCollections((prev) =>
          append ? mergeGroupedCollections(prev, mergedProducts) : productsToGroupedCollections(mergedProducts)
        );
        setCurrentPage(response.pagination?.currentPage ?? page);
        setHasNextPage(response.pagination?.hasNextPage ?? false);
      } catch (error) {
        console.error('Error fetching public fabric collections:', error);
        setError(error.response?.data?.message || 'Failed to load fabric collections');
        if (!append) {
          setCollections([]);
        }
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [vendorIdentifier, searchQuery, visualSearchImageUrl, sellerSearchFilters]
  );

  const fetchLooseFabrics = useCallback(async () => {
    if (!vendorIdentifier) return;
    setLooseFabricsLoading(true);
    try {
      const data = await getPublicProductsByPhone(vendorIdentifier, 1, 50, '');
      const products = Array.isArray(data?.products) ? data.products : [];
      const loose = products.filter((p) => !p?.fabricCollection);
      setLoosePublicFabrics(loose);
    } catch (e) {
      console.warn('Failed to load loose public fabrics:', e);
      setLoosePublicFabrics([]);
    } finally {
      setLooseFabricsLoading(false);
    }
  }, [vendorIdentifier]);

  const fetchInspirationDesigns = useCallback(
    async (page = 1, append = false) => {
      if (!vendorIdentifier) return;
      if (append) {
        setInspirationLoadingMore(true);
      } else {
        setInspirationLoading(true);
      }
      try {
        const data = await getPublicInspirationalDesignsByPhone(vendorIdentifier, page, 24);
        const raw = data.inspirationalDesigns || [];
        // Public API already filters server-side; only drop rows explicitly private.
        // If `isPublic` is omitted from the JSON (older API), treat as public so we don't hide valid results.
        const designs = raw.filter((d) => d && d.isPublic !== false);
        if (append) {
          setInspirationDesigns((prev) => {
            const ids = new Set(prev.map((p) => String(p._id)));
            const next = [...prev];
            for (const p of designs) {
              if (!ids.has(String(p._id))) next.push(p);
            }
            return next;
          });
        } else {
          setInspirationDesigns(designs);
        }
        setInspirationPage(data.pagination?.currentPage ?? page);
        setInspirationHasNextPage(Boolean(data.pagination?.hasNextPage));
      } catch (e) {
        console.error('Error fetching public inspirational designs:', e);
        if (!append) setInspirationDesigns([]);
      } finally {
        setInspirationLoading(false);
        setInspirationLoadingMore(false);
      }
    },
    [vendorIdentifier]
  );

  const fetchPublicKits = useCallback(
    async (page = 1, append = false) => {
      if (!vendorIdentifier) return;
      if (append) {
        setKitLoadingMore(true);
      } else {
        setKitLoading(true);
      }
      try {
        const data = await getPublicKitsByPhone(vendorIdentifier, page, 24, '');
        const kits = data.kits || [];
        if (append) {
          setPublicKits((prev) => {
            const ids = new Set(prev.map((k) => String(k._id)));
            const next = [...prev];
            for (const k of kits) {
              if (!ids.has(String(k._id))) next.push(k);
            }
            return next;
          });
        } else {
          setPublicKits(kits);
        }
        setKitPage(data.pagination?.currentPage ?? page);
        setKitHasNextPage(Boolean(data.pagination?.hasNextPage));
      } catch (e) {
        console.error('Error fetching public kits by phone:', e);
        if (!append) setPublicKits([]);
      } finally {
        setKitLoading(false);
        setKitLoadingMore(false);
      }
    },
    [vendorIdentifier]
  );

  useEffect(() => {
    const loadOwnerProfile = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await getUserProfile(token);
        const userData = res.data.data.user;
        setCurrentUser(userData);
      } catch (err) {
        console.error('Error loading current user profile:', err);
      }
    };
    loadOwnerProfile();
  }, []);

  useEffect(() => {
    if (!userInfo || !currentUser) return;
    const targetPhone = (userInfo.phone || '').replace(/\D/g, '');
    const currentPhone = (currentUser.phone || '').replace(/\D/g, '');
    const owner = Boolean(targetPhone && currentPhone && targetPhone === currentPhone);
    setIsOwner(owner);
    if (!owner) {
      setIsAdminView(false);
      setEditingSection(null);
    }
    if (owner) {
      hydrateDraftFromProfile(userInfo.publicProfile || {});
    }
  }, [userInfo, currentUser]);

  useEffect(() => {
    if (defaultAdminView && isOwner) {
      setIsAdminView(true);
    }
  }, [defaultAdminView, isOwner]);

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(searchInput);
      setCurrentPage(1);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput]);

  useEffect(() => {
    if (!vendorIdentifier) return;
    fetchCollections(1, false);
  }, [vendorIdentifier, fetchCollections]);

  useEffect(() => {
    if (activeTab !== 'collections' || collectionsSubTab !== 'bodyCuts' || !vendorIdentifier) return;
    fetchInspirationDesigns(1, false);
  }, [activeTab, collectionsSubTab, vendorIdentifier, fetchInspirationDesigns]);

  useEffect(() => {
    if (activeTab !== 'collections' || collectionsSubTab !== 'aiCreations' || !vendorIdentifier) return;
    fetchPublicKits(1, false);
  }, [activeTab, collectionsSubTab, vendorIdentifier, fetchPublicKits]);

  useEffect(() => {
    if (!showImageSearchOptions) return;
    const handleOutsideClick = (event) => {
      if (imageSearchMenuRef.current && !imageSearchMenuRef.current.contains(event.target)) {
        setShowImageSearchOptions(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showImageSearchOptions]);

  useEffect(() => {
    return () => {
      if (visualSearchImagePreview?.startsWith('blob:')) {
        URL.revokeObjectURL(visualSearchImagePreview);
      }
    };
  }, [visualSearchImagePreview]);

  const loadMoreCollectionsContent = useCallback(() => {
    if (activeTab !== 'collections') return;
    if (collectionsSubTab === 'fabrics' && hasNextPage && !isLoadingMore && !isLoading) {
      fetchCollections(currentPage + 1, true);
    } else if (collectionsSubTab === 'bodyCuts' && inspirationHasNextPage && !inspirationLoadingMore && !inspirationLoading) {
      fetchInspirationDesigns(inspirationPage + 1, true);
    } else if (collectionsSubTab === 'aiCreations' && kitHasNextPage && !kitLoadingMore && !kitLoading) {
      fetchPublicKits(kitPage + 1, true);
    }
  }, [
    activeTab,
    collectionsSubTab,
    hasNextPage,
    isLoadingMore,
    isLoading,
    currentPage,
    fetchCollections,
    inspirationHasNextPage,
    inspirationLoadingMore,
    inspirationLoading,
    inspirationPage,
    fetchInspirationDesigns,
    kitHasNextPage,
    kitLoadingMore,
    kitLoading,
    kitPage,
    fetchPublicKits
  ]);

  const canLoadMoreCollections = useMemo(() => {
    if (activeTab !== 'collections') return false;
    if (collectionsSubTab === 'fabrics') return Boolean(hasNextPage && !isLoadingMore && !isLoading);
    if (collectionsSubTab === 'bodyCuts') return Boolean(inspirationHasNextPage && !inspirationLoadingMore && !inspirationLoading);
    if (collectionsSubTab === 'aiCreations') return Boolean(kitHasNextPage && !kitLoadingMore && !kitLoading);
    return false;
  }, [
    activeTab,
    collectionsSubTab,
    hasNextPage,
    isLoadingMore,
    isLoading,
    inspirationHasNextPage,
    inspirationLoadingMore,
    inspirationLoading,
    kitHasNextPage,
    kitLoadingMore,
    kitLoading
  ]);

  // Infinite scroll with improved observer setup
  useEffect(() => {
    if (!canLoadMoreCollections) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          loadMoreCollectionsContent();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px'
      }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
      observer.disconnect();
    };
  }, [canLoadMoreCollections, loadMoreCollectionsContent]);

  // Fallback scroll handler for better reliability (especially on mobile)
  useEffect(() => {
    if (!canLoadMoreCollections) {
      return;
    }

    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight;
        const clientHeight = window.innerHeight || document.documentElement.clientHeight;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 300;
        if (isNearBottom) {
          loadMoreCollectionsContent();
        }
      }, 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      clearTimeout(scrollTimeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [canLoadMoreCollections, loadMoreCollectionsContent]);

  // Handle collection click - show collection detail page
  const handleCollectionClick = async (collection) => {
    const id = collection?._id;
    if (!id) return;
    try {
      const data = await getPublicCollectionById(id);
      const full = data?.collection ? { ...data.collection, childCollections: data.childCollections || [] } : null;
      setSelectedCollection(full || collection);
    } catch (e) {
      console.warn('Failed to load collection details:', e);
      setSelectedCollection(collection);
    }
  };

  // Handle back from collection detail
  const handleBackFromCollection = () => {
    setSelectedCollection(null);
  };

  // Handle fabric catalogue view
  const handleViewCatalogue = (fabric) => {
    setCatalogueFabric(fabric);
  };

  const handleSubscribe = async () => {
    // Get phone number from props or userInfo
    const targetPhoneNumber = resolvedPhoneDigits || (userInfo && userInfo.phone ? userInfo.phone.replace(/\D/g, '') : null);
    
    if (!targetPhoneNumber) {
      alertMessage('Unable to find collection owner information');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      // User is not logged in - store subscription info and open login modal
      const pendingSubscription = {
        phoneNumber: targetPhoneNumber,
        source: 'public-fabric'
      };
      localStorage.setItem('pendingSubscription', JSON.stringify(pendingSubscription));
      console.log('?? Stored pending subscription:', pendingSubscription);
      
      // Open login modal
      if (onOpenLoginModal && typeof onOpenLoginModal === 'function') {
        onOpenLoginModal();
      } else {
        alertMessage('Please login to subscribe. Your subscription will be completed after login.');
        if (setActiveView && typeof setActiveView === 'function') {
          setActiveView('login');
        }
      }
      return;
    }

    // User is logged in - directly subscribe
    setIsSubscribing(true);
    try {
      await subscribeToCollection(targetPhoneNumber, token);
      
      alertMessage('Successfully subscribed! You will receive updates on new collections.');
      window.location.href = '/';
    } catch (error) {
      console.error('Subscription error:', error);
      if (error.response?.status === 401) {
        // Token expired - store pending subscription and open login modal
        const pendingSubscription = {
          phoneNumber: targetPhoneNumber,
          source: 'public-fabric'
        };
        localStorage.setItem('pendingSubscription', JSON.stringify(pendingSubscription));
        
        if (onOpenLoginModal && typeof onOpenLoginModal === 'function') {
          onOpenLoginModal();
        }
      }

      const backendMessage = error.response?.data?.message || error.message;
      if (backendMessage?.toLowerCase().includes('already subscribed')) {
        alertMessage('Seller already subscribed.');
      } else {
        alertMessage(backendMessage || 'Failed to subscribe. Please try again later.');
      }
    } finally {
      setIsSubscribing(false);
    }
  };

  // Handle generate with model
  const handleGenerateWithModel = (product) => {
    const token = localStorage.getItem('token');
    
    // Prepare all variants for auto-selection
    const allVariants = [];
    
    // Add primary image as first variant
    const primaryImageUrl = product.images?.original?.s3Url || product.images?.original?.imgbbUrl || product.images?.original?.url || product.image;
    if (primaryImageUrl) {
      allVariants.push({
        image: { s3Url: primaryImageUrl },
        colorName: 'Primary',
        isPrimary: true
      });
    }
    
    // Add all color variants
    if (product.colorVariants && product.colorVariants.length > 0) {
      product.colorVariants.forEach((variant, index) => {
        allVariants.push({
          ...variant,
          isPrimary: false,
          originalIndex: index
        });
      });
    }
    
    // Prepare product details for localStorage
    const productDetails = {
      name: product.name || '',
      description: product.description || '',
      image: primaryImageUrl || '',
      category: product.category || '',
      type: product.type || '',
      targetAudience: product.targetAudience || 'General Audience',
      fabricAnalysis: product.fabricAnalysis || null,
      selectedProduct: product,
      colorVariants: product.colorVariants || [],
      selectedVariants: allVariants,
      selectedVariantIndex: 0
    };
    
    // Store fabric in localStorage for auto-selection after login/navigation
    localStorage.setItem('selectedFabricForGeneration', JSON.stringify(productDetails));
    
    if (token) {
      // User is logged in - navigate to home page which will trigger fabric auto-selection
      console.log('?? User logged in, navigating to home page with fabric in localStorage');
      window.location.href = '/';
    } else {
      // User is not logged in - open login modal
      if (onOpenLoginModal) {
        onOpenLoginModal();
      } else {
        // Fallback: navigate to login view
        setActiveView('login');
      }
      
      if (showNotification) {
        showNotification('Please login to generate kits with this fabric', 'info');
      }
    }
  };

  const getFabricImageFromCollection = (fabric) => {
    if (!fabric) return null;
    return (
      fabric.image ||
      fabric.images?.original?.s3Url ||
      fabric.images?.original?.imgbbUrl ||
      fabric.images?.original?.url ||
      null
    );
  };

  const getCollectionPreviewImage = (collection) => {
    if (!collection) return null;
    const coverImage = collection.image?.url || collection.image || null;
    if (coverImage) return coverImage;

    const firstFabricImage = (collection.fabrics || [])
      .map(getFabricImageFromCollection)
      .find(Boolean);

    return firstFabricImage || null;
  };

  // Custom Fabric Collection Card Component - Memoized
  const FabricCollectionCard = React.memo(({ collection }) => {
    const collectionImage = getCollectionPreviewImage(collection);
    const fabricCount = collection.fabrics?.length || 0;
    const tags = collection.tags || [];
    const propertyEntries = (collection.properties || []).map((prop) => {
      if (prop.key && prop.value) return `${prop.key}: ${prop.value}`;
      return prop.key || prop.value || '';
    }).filter(Boolean);
    const summary = propertyEntries.length
      ? propertyEntries.slice(0, 2).join(' • ')
      : 'No custom properties yet';
    
    return (
      <div
        className="group bg-white rounded-lg sm:rounded-xl border border-gray-200 hover:border-[var(--pv-primary)] hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer"
        onClick={() => handleCollectionClick(collection)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCollectionClick(collection);
          }
        }}
      >
        <div
          className="relative aspect-square flex items-center justify-center overflow-hidden"
          style={{
            background: 'linear-gradient(to bottom right, var(--pv-collection-from), var(--pv-collection-via), var(--pv-collection-to))',
          }}
        >
          {collectionImage ? (
            <LazyImage
              src={toThumbnailUrl(collectionImage)}
              fallbackSrc={collectionImage}
              alt={collection.name}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className="flex flex-col items-center justify-center w-full h-full px-4">
              <span className="text-lg sm:text-xl font-bold text-[var(--pv-primary)] text-center break-words">
                {collection.name}
              </span>
            </div>
          )}
          <div className="absolute top-1 left-1 sm:top-2 sm:left-2 bg-[var(--pv-primary)] text-white text-xs px-2 py-0.5 sm:px-3 sm:py-1 rounded-full font-semibold shadow">
            Collection
          </div>
          {fabricCount > 0 && (
            <div className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-[var(--pv-badge-secondary)] text-white text-xs font-semibold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full shadow">
              {fabricCount} fabric{fabricCount === 1 ? '' : 's'}
            </div>
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/25 opacity-0 group-hover:opacity-100 transition-all duration-300">
            <div className="bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg transform scale-90 group-hover:scale-100 transition-transform">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                <Layers size={16} />
                <span>View Collection</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-3 sm:p-4 space-y-2">
          <h3 className="font-semibold text-gray-800 line-clamp-1 text-sm sm:text-base">{collection.name}</h3>
          <p className="text-xs sm:text-sm text-gray-500 line-clamp-2">{summary}</p>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {tags.slice(0, 4).map(tag => (
                <span
                  key={tag}
                  className="text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-medium bg-[var(--pv-tag-bg)] text-[var(--pv-tag-text)]"
                >
                  #{tag}
                </span>
              ))}
              {tags.length > 4 && (
                <span className="text-[10px] sm:text-xs text-[var(--pv-primary)] font-semibold">
                  +{tags.length - 4}
                </span>
              )}
            </div>
          )}
          <div className="flex items-center justify-between text-[11px] sm:text-xs text-gray-400">
            <span className="text-[var(--pv-primary)] font-medium">
              {fabricCount} fabric{fabricCount === 1 ? '' : 's'}
            </span>
          </div>
        </div>
      </div>
    );
  }, (prevProps, nextProps) => {
    return prevProps.collection._id === nextProps.collection._id &&
           prevProps.collection.name === nextProps.collection.name &&
           getCollectionPreviewImage(prevProps.collection) === getCollectionPreviewImage(nextProps.collection);
  });

  // Show collection detail view
  if (selectedCollection) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-[var(--pv-page-from)] via-[var(--pv-page-via)] to-[var(--pv-page-to)]"
        style={vendorCssVars}
      >
        <PublicFabricCollectionDetailView
          collection={selectedCollection}
          onBack={handleBackFromCollection}
          onViewCatalogue={setCatalogueFabric}
          onGenerateWithModel={handleGenerateWithModel}
          showNotification={showNotification}
          onOpenLoginModal={onOpenLoginModal}
          onOpenChildCollection={handleCollectionClick}
        />
      </div>
    );
  }

  // Show loose fabric detail view
  if (selectedLooseFabric) {
    return (
      <FabricCatalogueView
        fabric={selectedLooseFabric}
        onClose={() => setSelectedLooseFabric(null)}
        showNotification={showNotification}
        allowShadeCardUpload={false}
        onGenerateFabric={handleGenerateWithModel}
      />
    );
  }

  // Show catalogue view
  if (catalogueFabric) {
    return (
      <FabricCatalogueView
        fabric={catalogueFabric}
        onClose={() => setCatalogueFabric(null)}
        showNotification={showNotification}
      />
    );
  }

  // Error state
  if (error && !isLoading) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-[var(--pv-page-from)] via-[var(--pv-page-via)] to-[var(--pv-page-to)] flex items-center justify-center px-4"
        style={vendorCssVars}
      >
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-6 text-center">
          <Layers size={48} className="text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Fabric Collections Not Found</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => setActiveView('home')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-[var(--pv-page-from)] via-[var(--pv-page-via)] to-[var(--pv-page-to)]"
      style={vendorCssVars}
    >
      <div className={`${containerClassName} mx-auto ${containerPaddingClassName} py-4 sm:py-6 w-full overflow-x-hidden`}>
        {/* Tabs + Admin Toggle */}
        <div className="flex flex-col gap-3 mb-4 sm:mb-6 max-w-full">
          <div className="inline-flex rounded-full bg-white shadow-sm border border-[var(--pv-tab-ring)] p-1 self-center max-w-full">
            <button
              onClick={() => setActiveTab('profile')}
              className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-full transition ${
                activeTab === 'profile' ? 'text-white shadow' : 'hover:bg-[var(--pv-tab-inactive-hover-bg)]'
              }`}
              style={
                activeTab === 'profile'
                  ? { backgroundColor: 'var(--pv-primary)', color: '#fff' }
                  : { color: 'var(--pv-tab-inactive)' }
              }
            >
              Profile
            </button>
            <button
              onClick={() => setActiveTab('collections')}
              className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-full transition ${
                activeTab === 'collections' ? 'text-white shadow' : 'hover:bg-[var(--pv-tab-inactive-hover-bg)]'
              }`}
              style={
                activeTab === 'collections'
                  ? { backgroundColor: 'var(--pv-primary)', color: '#fff' }
                  : { color: 'var(--pv-tab-inactive)' }
              }
            >
              Collections
            </button>
          </div>
          {isOwner && (
            <button
              onClick={() => {
                setIsAdminView(prev => !prev);
                if (isAdminView) {
                  setEditingSection(null);
                }
              }}
              className={`px-4 py-2 rounded-full text-xs sm:text-sm font-semibold transition shadow-sm self-center max-w-full ${
                isAdminView
                  ? 'text-white hover:opacity-90'
                  : 'bg-white border hover:bg-[var(--pv-tab-inactive-hover-bg)]'
              }`}
              style={
                isAdminView
                  ? { backgroundColor: 'var(--pv-primary)' }
                  : { color: 'var(--pv-tab-inactive)', borderColor: 'var(--pv-tab-ring)' }
              }
            >
              {isAdminView ? 'Exit Admin View' : 'View as Admin'}
            </button>
          )}
        </div>

        {activeTab === 'profile' ? (
          <>
        {/* Seller Hero */}
        <div
          className="relative overflow-hidden rounded-2xl sm:rounded-3xl text-white shadow-2xl mb-6 max-w-full"
          style={{
            background: 'linear-gradient(to right, var(--pv-hero-from), var(--pv-hero-via), var(--pv-hero-to))',
          }}
        >
          {displaySeller.coverUrl && (
            <div
              className="absolute inset-0 bg-center bg-cover"
              style={{ backgroundImage: `url(${displaySeller.coverUrl})` }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/20 to-black/10" />
          <div className="relative z-10 p-4 sm:p-6 lg:p-8 max-w-full overflow-hidden">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4 sm:gap-6 max-w-full">
              <div className="flex flex-col gap-4 max-w-full min-w-0 w-full">
                <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl bg-white/15 border border-white/30 flex items-center justify-center text-xl sm:text-2xl font-bold flex-shrink-0">
                  {displaySeller.avatarUrl ? (
                    <img
                      src={displaySeller.avatarUrl}
                      alt={displaySeller.name}
                      className="h-full w-full rounded-2xl object-cover"
                    />
                  ) : (
                    displaySeller.brandName.charAt(0)
                  )}
                </div>
                <div className="max-w-full min-w-0 w-full">
                  {editingSection === 'hero' ? (
                    <div className="space-y-2 max-w-full w-full">
                      <input
                        value={publicProfileDraft.brandName}
                        onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, brandName: e.target.value }))}
                        placeholder="Brand name"
                        className="w-full max-w-full rounded-lg px-3 py-2 text-sm text-gray-900"
                      />
                      <input
                        value={publicProfileDraft.tagline}
                        onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, tagline: e.target.value }))}
                        placeholder="Tagline"
                        className="w-full max-w-full rounded-lg px-3 py-2 text-sm text-gray-900"
                      />
                      <div className="flex flex-col gap-2 max-w-full">
                        <input
                          value={publicProfileDraft.location}
                          onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, location: e.target.value }))}
                          placeholder="Location"
                          className="w-full max-w-full rounded-lg px-3 py-2 text-sm text-gray-900"
                        />
                        <input
                          value={publicProfileDraft.address}
                          onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, address: e.target.value }))}
                          placeholder="Address"
                          className="w-full max-w-full rounded-lg px-3 py-2 text-sm text-gray-900"
                        />
                        <input
                          value={publicProfileDraft.since}
                          onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, since: e.target.value }))}
                          placeholder="Since"
                          className="w-full max-w-full rounded-lg px-3 py-2 text-sm text-gray-900"
                        />
                      </div>
                      <div className="flex flex-col gap-2 max-w-full">
                        <input
                          value={publicProfileDraft.avatarUrl}
                          onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, avatarUrl: e.target.value }))}
                          placeholder="Avatar image URL"
                          className="w-full max-w-full rounded-lg px-3 py-2 text-sm text-gray-900"
                        />
                        <button
                          type="button"
                          onClick={() => avatarInputRef.current?.click()}
                          disabled={isUploadingAvatar}
                          className="w-full max-w-full rounded-lg px-3 py-2 text-sm font-semibold bg-white/90 text-gray-900 hover:bg-white transition disabled:opacity-60"
                        >
                          {isUploadingAvatar ? 'Uploading...' : 'Upload Avatar'}
                        </button>
                      </div>
                      <div className="flex flex-col gap-2 max-w-full">
                        <input
                          value={publicProfileDraft.coverUrl}
                          onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, coverUrl: e.target.value }))}
                          placeholder="Cover image URL"
                          className="w-full max-w-full rounded-lg px-3 py-2 text-sm text-gray-900"
                        />
                        <button
                          type="button"
                          onClick={() => coverInputRef.current?.click()}
                          disabled={isUploadingCover}
                          className="w-full max-w-full rounded-lg px-3 py-2 text-sm font-semibold bg-white/90 text-gray-900 hover:bg-white transition disabled:opacity-60"
                        >
                          {isUploadingCover ? 'Uploading...' : 'Upload Cover'}
                        </button>
                      </div>
                      <input
                        ref={avatarInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload('avatar', e.target.files?.[0])}
                      />
                      <input
                        ref={coverInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload('cover', e.target.files?.[0])}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-wrap items-center gap-2 max-w-full">
                        {displaySeller.brandName && <h2 className="text-xl sm:text-3xl font-bold break-words">{displaySeller.brandName}</h2>}
                        {userInfo?.isVendorVerified ? (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-500/25 border border-emerald-300/40 px-2 py-1 text-xs font-semibold flex-shrink-0 text-white"
                            title="Verified by Fashdeck"
                          >
                            <BadgeCheck size={14} className="text-emerald-200" aria-hidden />
                            Verified
                          </span>
                        ) : null}
                      </div>
                      {displaySeller.tagline && <p className="text-sm sm:text-base text-white/90 mt-1 break-words">{displaySeller.tagline}</p>}
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-3 text-xs sm:text-sm text-white/80">
                        {displaySeller.location && <span className="inline-flex items-center gap-1 flex-shrink-0"><MapPin size={14} /><span className="break-words">{displaySeller.location}</span></span>}
                        {displaySeller.rating != null && (
                          <span className="inline-flex items-center gap-1 flex-shrink-0">
                            <Star size={14} className="fill-amber-300 text-amber-300" />
                            {displaySeller.rating}
                            {displaySeller.reviews > 0 ? ` (${displaySeller.reviews})` : ''}
                          </span>
                        )}
                        {displaySeller.since && <span className="inline-flex items-center gap-1 flex-shrink-0">Since {displaySeller.since}</span>}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2 w-full lg:w-auto min-w-0">
                {isOwner && isAdminView && (
                  <div className="flex flex-col gap-2 w-full min-w-0">
                    {editingSection === 'hero' ? (
                      <>
                        <button
                          onClick={handleSavePublicProfile}
                          disabled={isSavingProfile}
                          className="inline-flex items-center justify-center gap-1 bg-white text-gray-900 px-3 py-2 rounded-xl text-sm font-semibold shadow hover:bg-gray-100 transition disabled:opacity-60 w-full"
                        >
                          {isSavingProfile ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => {
                            hydrateDraftFromProfile(userInfo?.publicProfile || {});
                            setEditingSection(null);
                          }}
                          className="inline-flex items-center justify-center gap-1 bg-white/20 text-white px-3 py-2 rounded-xl text-sm font-semibold shadow hover:bg-white/30 transition w-full"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setEditingSection('hero')}
                        className="inline-flex items-center justify-center gap-1 bg-white/20 text-white px-3 py-2 rounded-xl text-sm font-semibold shadow hover:bg-white/30 transition w-full"
                      >
                        Edit Header
                      </button>
                    )}
                  </div>
                )}
                <button
                  onClick={handleSubscribe}
                  disabled={isSubscribing}
                  className="inline-flex items-center justify-center gap-1 bg-white text-gray-900 px-3 py-2 rounded-xl text-sm font-semibold shadow hover:bg-gray-100 transition disabled:opacity-60 w-full"
                >
                  <Bell size={14} />
                  <span className="truncate">{isSubscribing ? 'Subscribing...' : 'Follow'}</span>
                </button>
                {displaySeller.socials.whatsapp && (
                  <a
                    href={displaySeller.socials.whatsapp}
                    className="inline-flex items-center justify-center gap-1 bg-black/30 text-white px-3 py-2 rounded-xl text-sm font-semibold shadow hover:bg-black/40 transition w-full"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <MessageCircle size={14} />
                    <span className="truncate">WhatsApp</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Seller Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 max-w-full">
          <div className="bg-white rounded-2xl shadow-lg border border-[var(--pv-card-border)] p-4 sm:p-5 lg:col-span-2 max-w-full min-w-0 overflow-hidden">
            <div className="flex items-center justify-between gap-2 mb-2 min-w-0">
              <h3 className="text-lg font-semibold text-gray-800 truncate">About Us</h3>
              {isOwner && isAdminView && (
                editingSection === 'about' ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSavePublicProfile}
                      disabled={isSavingProfile}
                      className="px-3 py-1.5 text-xs bg-[var(--pv-primary)] text-white rounded-lg hover:bg-[var(--pv-primary-hover)] disabled:opacity-60"
                    >
                      {isSavingProfile ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        hydrateDraftFromProfile(userInfo?.publicProfile || {});
                        setEditingSection(null);
                      }}
                      className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setEditingSection('about')}
                    className="px-3 py-1.5 text-xs rounded-lg bg-[var(--pv-tag-bg)] text-[var(--pv-tag-text)] hover:opacity-90"
                  >
                    Edit
                  </button>
                )
              )}
            </div>
            {editingSection === 'about' ? (
              <div className="space-y-3">
                <textarea
                  value={publicProfileDraft.bio}
                  onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, bio: e.target.value }))}
                  rows={4}
                  placeholder="Tell buyers about your brand"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800"
                />
                <input
                  value={publicProfileDraft.specialties}
                  onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, specialties: e.target.value }))}
                  placeholder="Specialties (comma separated)"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800"
                />
              </div>
            ) : (
              <>
                {displaySeller.bio ? (
                  <p className="text-sm text-gray-600 mb-4 whitespace-pre-line">{displaySeller.bio}</p>
                ) : (
                  <p className="text-sm text-gray-400 italic mb-4">
                    {isOwner && isAdminView
                      ? 'Add an About Us description so visitors know your story.'
                      : 'No About Us details yet.'}
                  </p>
                )}
                {displaySeller.specialties.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {displaySeller.specialties.map((item) => (
                      <span
                        key={item}
                        className="px-3 py-1 rounded-full text-xs font-semibold bg-[var(--pv-tag-bg)] text-[var(--pv-tag-text)]"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-6">
              {stats.map((stat) => (
                <div key={stat.label} className="rounded-xl border border-gray-100 bg-gray-50 p-3 sm:p-4 text-center">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg border border-[var(--pv-card-border)] p-4 sm:p-5 max-w-full min-w-0 overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Contact</h3>
              {isOwner && isAdminView && (
                editingSection === 'contact' ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSavePublicProfile}
                      disabled={isSavingProfile}
                      className="px-3 py-1.5 text-xs bg-[var(--pv-primary)] text-white rounded-lg hover:bg-[var(--pv-primary-hover)] disabled:opacity-60"
                    >
                      {isSavingProfile ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        hydrateDraftFromProfile(userInfo?.publicProfile || {});
                        setEditingSection(null);
                      }}
                      className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setEditingSection('contact')}
                    className="px-3 py-1.5 text-xs rounded-lg bg-[var(--pv-tag-bg)] text-[var(--pv-tag-text)] hover:opacity-90"
                  >
                    Edit
                  </button>
                )
              )}
            </div>
            {editingSection === 'contact' ? (
              <div className="space-y-3">
                <input
                  value={publicProfileDraft.location}
                  onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Location (City, State)"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800"
                />
                <input
                  value={publicProfileDraft.address}
                  onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Address"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800"
                />
                <input
                  value={publicProfileDraft.contactEmail}
                  onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, contactEmail: e.target.value }))}
                  placeholder="Contact email"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800"
                />
                <input
                  value={publicProfileDraft.website}
                  onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="Website"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800"
                />
                <input
                  value={publicProfileDraft.instagram}
                  onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, instagram: e.target.value }))}
                  placeholder="Instagram URL"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800"
                />
                <input
                  value={publicProfileDraft.facebook}
                  onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, facebook: e.target.value }))}
                  placeholder="Facebook URL"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800"
                />
                <input
                  value={publicProfileDraft.linkedin}
                  onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, linkedin: e.target.value }))}
                  placeholder="LinkedIn URL"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800"
                />
                <input
                  value={publicProfileDraft.whatsapp}
                  onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, whatsapp: e.target.value }))}
                  placeholder="WhatsApp link"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800"
                />
                <input
                  value={publicProfileDraft.services}
                  onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, services: e.target.value }))}
                  placeholder="Services (comma separated)"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800"
                />
                <input
                  value={publicProfileDraft.businessPhotos}
                  onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, businessPhotos: e.target.value }))}
                  placeholder="Business photo URLs (comma separated)"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800"
                />
                <textarea
                  value={publicProfileDraft.googleReviews}
                  onChange={(e) => setPublicProfileDraft(prev => ({ ...prev, googleReviews: e.target.value }))}
                  placeholder="Google reviews (one per line: Name | Rating | Review)"
                  rows={4}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800"
                />
              </div>
            ) : (
              <>
                <div className="space-y-3 text-sm text-gray-600 max-w-full">
                  {displaySeller.location && (
                    <div className="flex items-start gap-2 min-w-0">
                      <MapPin size={16} className="text-[var(--pv-primary)] mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">Location</p>
                        <p className="break-words">{displaySeller.location}</p>
                      </div>
                    </div>
                  )}
                  {displaySeller.address && (
                    <div className="flex items-start gap-2 min-w-0">
                      <MapPin size={16} className="text-[var(--pv-primary)] mt-0.5 flex-shrink-0 opacity-70" />
                      <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">Address</p>
                        <p className="break-words">{displaySeller.address}</p>
                      </div>
                    </div>
                  )}
                  {displaySeller.phone && (
                    <div className="flex items-center gap-2 min-w-0">
                      <Phone size={16} className="text-[var(--pv-primary)] flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">Contact</p>
                        <a href={`tel:${String(displaySeller.phone).replace(/\s+/g, '')}`} className="break-all hover:text-[var(--pv-primary)]">
                          {displaySeller.phone}
                        </a>
                      </div>
                    </div>
                  )}
                  {displaySeller.socials.whatsapp && (
                    <div className="flex items-center gap-2 min-w-0">
                      <MessageCircle size={16} className="text-[var(--pv-primary)] flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">WhatsApp</p>
                        <a
                          href={displaySeller.socials.whatsapp}
                          target="_blank"
                          rel="noreferrer"
                          className="break-all text-[var(--pv-primary)] font-medium hover:underline"
                        >
                          Chat on WhatsApp
                        </a>
                      </div>
                    </div>
                  )}
                  {displaySeller.email && (
                    <div className="flex items-center gap-2 min-w-0">
                      <Mail size={16} className="text-[var(--pv-primary)] flex-shrink-0" />
                      <span className="break-all">{displaySeller.email}</span>
                    </div>
                  )}
                  {displaySeller.website && (
                    <div className="flex items-center gap-2 min-w-0">
                      <Globe size={16} className="text-[var(--pv-primary)] flex-shrink-0" />
                      <span className="break-all">{displaySeller.website}</span>
                    </div>
                  )}
                  {!displaySeller.location && !displaySeller.address && !displaySeller.phone && !displaySeller.socials.whatsapp && !displaySeller.email && !displaySeller.website && (
                    <p className="text-sm text-gray-400 italic">
                      {isOwner && isAdminView
                        ? 'Add location and contact details for buyers.'
                        : 'No contact details listed yet.'}
                    </p>
                  )}
                </div>
                {(displaySeller.socials.instagram || displaySeller.socials.facebook || displaySeller.socials.linkedin) && (
                  <div className="flex gap-3 mt-5">
                    {displaySeller.socials.instagram && (
                      <a href={displaySeller.socials.instagram} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-[var(--pv-tag-bg)] text-[var(--pv-primary)] hover:opacity-90 transition">
                        <Instagram size={16} />
                      </a>
                    )}
                    {displaySeller.socials.facebook && (
                      <a href={displaySeller.socials.facebook} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-[var(--pv-tag-bg)] text-[var(--pv-primary)] hover:opacity-90 transition">
                        <Facebook size={16} />
                      </a>
                    )}
                    {displaySeller.socials.linkedin && (
                      <a href={displaySeller.socials.linkedin} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-[var(--pv-tag-bg)] text-[var(--pv-primary)] hover:opacity-90 transition">
                        <Linkedin size={16} />
                      </a>
                    )}
                  </div>
                )}
                {displaySeller.services.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Services</h4>
                    <ul className="text-xs text-gray-600 space-y-1">
                      {displaySeller.services.map((service) => (
                        <li key={service} className="flex items-center gap-2">
                          <span className="h-1.5 w-1.5 rounded-full bg-[var(--pv-primary)] opacity-60" />
                        {service}
                      </li>
                    ))}
                  </ul>
                </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Business Photos - Only show if photos exist or in admin view */}
        {(displaySeller.businessPhotos.length > 0 || (isOwner && isAdminView)) && (
          <div className="bg-white rounded-2xl shadow-lg border border-[var(--pv-card-border)] p-4 sm:p-6 mb-6 max-w-full overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Business Photos</h3>
              {isOwner && isAdminView && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => businessPhotosInputRef.current?.click()}
                    disabled={isUploadingBusinessPhotos}
                    className="px-3 py-1.5 text-xs bg-[var(--pv-primary)] text-white rounded-lg hover:bg-[var(--pv-primary-hover)] disabled:opacity-60 whitespace-nowrap"
                  >
                    {isUploadingBusinessPhotos ? 'Uploading...' : 'Upload'}
                  </button>
                  <button
                    onClick={() => setEditingSection('contact')}
                    className="px-3 py-1.5 text-xs rounded-lg whitespace-nowrap bg-[var(--pv-tag-bg)] text-[var(--pv-tag-text)] hover:opacity-90"
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
            <input
              ref={businessPhotosInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => handleBusinessPhotosUpload(e.target.files)}
            />
            
            {displaySeller.businessPhotos.length > 0 && (
              <>
                {/* Desktop & Tablet Grid View */}
                <div className="hidden sm:grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {displaySeller.businessPhotos.map((photo, index) => (
                    <div 
                      key={`${photo}-${index}`} 
                      className="aspect-[4/3] rounded-xl overflow-hidden border border-[var(--pv-card-border)] shadow-sm bg-gray-50 relative group"
                    >
                      <LazyImage
                        src={toThumbnailUrl(photo)}
                        fallbackSrc={photo}
                        alt={`Business Photo ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          console.error(`Failed to load business photo ${index + 1}`);
                        }}
                      />
                    </div>
                  ))}
                </div>
                
                {/* Mobile Horizontal Scroll View */}
                <div className="flex sm:hidden gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-purple-300 scrollbar-track-gray-100">
                  {displaySeller.businessPhotos.map((photo, index) => (
                    <div 
                      key={`${photo}-${index}`} 
                      className="min-w-[140px] max-w-[140px] h-36 rounded-xl overflow-hidden border border-[var(--pv-card-border)] shadow-sm bg-gray-50 flex-shrink-0 relative"
                    >
                      <LazyImage
                        src={toThumbnailUrl(photo)}
                        fallbackSrc={photo}
                        alt={`Business Photo ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.error(`Failed to load business photo ${index + 1}`);
                        }}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
            
            {displaySeller.businessPhotos.length === 0 && isOwner && isAdminView && (
              <p className="text-sm text-gray-500 text-center py-8">No business photos yet. Upload your first photo to showcase your business.</p>
            )}
          </div>
        )}

        {/* Reviews */}
        {(displaySeller.googleReviews.length > 0 || (isOwner && isAdminView)) && (
          <div className="bg-white rounded-2xl shadow-lg border border-[var(--pv-card-border)] p-4 sm:p-6 mb-6 max-w-full overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <div className="min-w-0">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Reviews</h3>
                {displaySeller.rating != null && displaySeller.googleReviews.length > 0 && (
                  <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                    {renderStars(displaySeller.rating, 16)}
                    <span className="font-semibold text-gray-800">{displaySeller.rating}</span>
                    <span className="text-gray-400">·</span>
                    <span>{displaySeller.googleReviews.length} review{displaySeller.googleReviews.length === 1 ? '' : 's'}</span>
                  </div>
                )}
              </div>
              {isOwner && isAdminView && (
                <button
                  onClick={() => setEditingSection('contact')}
                  className="px-3 py-1.5 text-xs rounded-lg whitespace-nowrap bg-[var(--pv-tag-bg)] text-[var(--pv-tag-text)] hover:opacity-90 self-start"
                >
                  Edit
                </button>
              )}
            </div>

            {displaySeller.googleReviews.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                {displaySeller.googleReviews.map((review, index) => (
                  <article
                    key={`${review.author || 'review'}-${index}`}
                    className="rounded-xl border border-gray-100 bg-gray-50/80 p-4"
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-800 truncate">{review.author || 'Customer'}</p>
                        {review.source && (
                          <p className="text-[11px] text-gray-400 mt-0.5">{review.source}</p>
                        )}
                      </div>
                      {review.rating != null && renderStars(review.rating, 14)}
                    </div>
                    {review.text && (
                      <p className="text-sm text-gray-600 leading-relaxed">{review.text}</p>
                    )}
                  </article>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">
                No reviews yet. Add Google reviews from the Contact edit form (one per line: Name | Rating | Review).
              </p>
            )}
          </div>
        )}

        
          </>
        ) : (
          <>
            <div className="flex flex-col items-center gap-2 sm:gap-3 mb-4 sm:mb-6 max-w-full px-1">
              <p className="text-[10px] sm:text-xs text-center text-gray-500 font-medium tracking-wide max-w-md leading-snug">
                <span className="text-[var(--pv-primary)]/90">Fabrics</span>
                <span className="mx-1 text-gray-400" aria-hidden>
                  +
                </span>
                <span className="text-[var(--pv-primary)]/90">body cuts</span>
                <span className="mx-1 text-gray-400" aria-hidden>
                  =
                </span>
                <span className="text-[var(--pv-primary)]/90">Generated concepts</span>
              </p>
              <div
                className="inline-flex flex-wrap items-center justify-center gap-y-2 gap-x-0.5 sm:gap-x-1 rounded-2xl bg-white/90 shadow-sm border border-[var(--pv-card-border)]/80 px-2 py-2 sm:px-3 sm:py-2.5"
                role="tablist"
                aria-label="Collection sections"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={collectionsSubTab === 'fabrics'}
                  onClick={() => {
                    setCollectionsSubTab('fabrics');
                    setCurrentPage(1);
                    // Always fetch latest public collections + loose public fabrics when Fabrics tab is clicked
                    fetchCollections(1, false, true);
                    fetchLooseFabrics();
                  }}
                  className={`px-2.5 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-full transition inline-flex items-center gap-1.5 ${
                    collectionsSubTab === 'fabrics'
                      ? 'text-white shadow-md ring-2 ring-black/10 bg-[var(--pv-primary)]'
                      : 'text-[var(--pv-tab-inactive)] hover:bg-[var(--pv-tab-inactive-hover-bg)]'
                  }`}
                >
                  <Layers size={14} className="sm:w-4 sm:h-4 shrink-0" />
                  <span>Fabrics</span>
                </button>
                <span
                  className="inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[var(--pv-tag-bg)] text-[var(--pv-primary)] border border-[var(--pv-card-border)] shrink-0"
                  aria-hidden="true"
                  title="plus"
                >
                  <Plus size={16} className="sm:w-[18px] sm:h-[18px] stroke-[2.5]" />
                </span>
                <button
                  type="button"
                  role="tab"
                  aria-selected={collectionsSubTab === 'bodyCuts'}
                  onClick={() => setCollectionsSubTab('bodyCuts')}
                  className={`px-2.5 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-full transition inline-flex items-center gap-1.5 ${
                    collectionsSubTab === 'bodyCuts'
                      ? 'text-white shadow-md ring-2 ring-black/10 bg-[var(--pv-primary)]'
                      : 'text-[var(--pv-tab-inactive)] hover:bg-[var(--pv-tab-inactive-hover-bg)]'
                  }`}
                >
                  <Shirt size={14} className="sm:w-4 sm:h-4 shrink-0" />
                  <span>Body cuts</span>
                </button>
                <span
                  className="inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[var(--pv-tag-bg)] text-[var(--pv-tag-text)] border border-[var(--pv-card-border)] shrink-0"
                  aria-hidden="true"
                  title="equals"
                >
                  <Equal size={16} className="sm:w-[18px] sm:h-[18px] stroke-[2.5]" />
                </span>
                <button
                  type="button"
                  role="tab"
                  aria-selected={collectionsSubTab === 'aiCreations'}
                  onClick={() => setCollectionsSubTab('aiCreations')}
                  className={`px-2.5 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-full transition inline-flex items-center gap-1.5 ${
                    collectionsSubTab === 'aiCreations'
                      ? 'text-white shadow-md ring-2 ring-black/10 bg-[var(--pv-primary)]'
                      : 'text-[var(--pv-tab-inactive)] hover:bg-[var(--pv-tab-inactive-hover-bg)]'
                  }`}
                >
                  <Sparkles size={14} className="sm:w-4 sm:h-4 shrink-0" />
                  <span className="whitespace-nowrap">Generated concepts</span>
                </button>
              </div>
            </div>

            {collectionsSubTab === 'fabrics' && (
              <>
            {/* Search: semantic text + image (same APIs as My Fabrics, scoped to this seller) */}
            <div className="mb-4 sm:mb-6 max-w-full space-y-3">
              <div className="flex flex-col lg:flex-row gap-3 lg:items-start lg:justify-between max-w-4xl w-full">
                <div className="relative w-full lg:flex-1 lg:max-w-xl">
                  <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search by fabric type, color, feel, garment…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    disabled={Boolean(visualSearchImageUrl)}
                    className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white shadow-sm disabled:bg-gray-50 disabled:text-gray-500"
                  />
                  {searchInput && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchInput('');
                        setSearchQuery('');
                        setCurrentPage(1);
                      }}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      aria-label="Clear search"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                <input
                  ref={visualSearchInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleVisualSearchImageSelect}
                />
                <input
                  ref={visualSearchCameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleVisualSearchCameraCapture}
                />
                <div ref={imageSearchMenuRef} className="relative w-full sm:w-auto shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowImageSearchOptions((v) => !v)}
                    disabled={isVisualSearchUploading}
                    className={`w-full sm:w-auto px-4 py-2.5 rounded-xl border text-sm font-semibold transition flex items-center justify-center gap-2 ${
                      isVisualSearchUploading
                        ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                    title="Search by fabric image"
                  >
                    {isVisualSearchUploading ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <ScanSearch size={16} />
                    )}
                    <span>{isVisualSearchUploading ? 'Uploading…' : 'Image search'}</span>
                  </button>
                  {showImageSearchOptions && !isVisualSearchUploading && (
                    <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-lg z-30 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => openImageSearchOption('camera')}
                        className="w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Camera size={16} className="text-blue-600" />
                        <span>Take photo</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => openImageSearchOption('upload')}
                        className="w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 border-t border-gray-100 flex items-center gap-2"
                      >
                        <Upload size={16} className="text-indigo-600" />
                        <span>Upload from device</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {visualSearchImageUrl && (
                <div className="flex flex-wrap items-center gap-3 px-1">
                  <div className="w-10 h-10 rounded-lg overflow-hidden border border-gray-200 bg-white shrink-0">
                    {visualSearchImagePreview ? (
                      <img src={visualSearchImagePreview} alt="Visual search" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon size={14} className="text-gray-400" />
                      </div>
                    )}
                  </div>
                  <span className="text-xs text-gray-600 font-medium">Searching by image similarity</span>
                  <button
                    type="button"
                    onClick={clearVisualSearch}
                    className="px-2 py-1 text-xs rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                  >
                    Clear image
                  </button>
                </div>
              )}
              <div className="text-xs text-gray-500 px-1 flex flex-wrap items-center gap-2">
                <span
                  className={`px-2 py-0.5 rounded-full font-semibold border ${
                    visualSearchImageUrl
                      ? 'bg-blue-50 text-blue-700 border-blue-100'
                      : 'bg-emerald-50 text-emerald-700 border-emerald-100'
                  }`}
                >
                  {visualSearchImageUrl ? 'Visual image search' : 'Semantic search'}
                </span>
                <span className="font-medium">Matches collections containing similar fabrics.</span>
              </div>
            </div>

        {/* Loose public fabrics (not inside any collection) */}
        {looseFabricsLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 size={18} className="animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">Loading fabrics…</span>
          </div>
        ) : loosePublicFabrics.length > 0 ? (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3 px-1">
              <h3 className="text-sm sm:text-base font-semibold text-gray-900">Fabrics (not in a collection)</h3>
              <span className="text-xs text-gray-500">{loosePublicFabrics.length}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 max-w-full">
              {loosePublicFabrics.map((fabric) => {
                const img =
                  fabric.image ||
                  fabric.images?.original?.s3Url ||
                  fabric.images?.original?.imgbbUrl ||
                  fabric.images?.original?.url ||
                  null;
                return (
                  <button
                    type="button"
                    key={fabric._id}
                    onClick={() => setSelectedLooseFabric(fabric)}
                    className="group text-left bg-white rounded-lg sm:rounded-xl border border-gray-200 hover:border-[var(--pv-primary)] hover:shadow-lg transition-all duration-300 overflow-hidden"
                    title="View fabric details"
                  >
                    <div className="relative aspect-square bg-gray-100 overflow-hidden">
                      {img ? (
                        <LazyImage
                          src={toThumbnailUrl(img)}
                          fallbackSrc={img}
                          alt={fabric.name || 'Fabric'}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <Layers size={22} />
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="font-semibold text-gray-800 line-clamp-1 text-sm">{fabric.name || 'Fabric'}</p>
                      <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{fabric.description || ''}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* Fabric Collections Grid */}
        {isLoading ? (
          visualSearchImageUrl ? (
            <div className="space-y-4 sm:space-y-6">
              <div className="relative overflow-hidden rounded-2xl border border-cyan-200 bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 p-5 sm:p-6 text-white shadow-lg">
                <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_20%,#22d3ee_0%,transparent_35%),radial-gradient(circle_at_80%_80%,#6366f1_0%,transparent_35%)]" />
                <div className="absolute inset-y-0 -left-1/3 w-1/2 bg-white/20 blur-2xl animate-[pulse_2.4s_ease-in-out_infinite]" />
                <div className="relative flex items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-cyan-200 font-semibold">Visual Search</p>
                    <h3 className="text-base sm:text-lg font-semibold mt-1">Scanning textile patterns and texture signatures</h3>
                    <p className="text-xs sm:text-sm text-blue-100/90 mt-1">Matching your input image against fabric vectors in real time.</p>
                  </div>
                  <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
                    <Loader2 size={20} className="animate-spin text-cyan-200" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 max-w-full">
                {Array.from({ length: 10 }).map((_, idx) => (
                  <div
                    key={`visual-search-skeleton-${idx}`}
                    className="rounded-xl border border-blue-100 bg-white/80 overflow-hidden shadow-sm"
                  >
                    <div className="aspect-square bg-gradient-to-br from-slate-200 via-blue-100 to-indigo-200 animate-pulse" />
                    <div className="p-3 space-y-2">
                      <div className="h-3 rounded bg-slate-200 animate-pulse" />
                      <div className="h-3 rounded bg-slate-100 w-2/3 animate-pulse" />
                      <div className="h-8 rounded-lg bg-gradient-to-r from-cyan-100 to-blue-100 animate-pulse mt-3" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12 sm:py-20">
              <div className="text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg sm:rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 animate-pulse">
                  <Layers size={20} className="text-white sm:w-6 sm:h-6" />
                </div>
                <p className="text-sm sm:text-base text-blue-600 font-medium">Loading fabric collections...</p>
              </div>
            </div>
          )
        ) : collections.length === 0 ? (
          <div className="text-center py-12 sm:py-20">
            <Layers size={48} className="text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">
              {searchQuery.trim() || visualSearchImageUrl
                ? 'No collections match this search yet. Try different words or another image.'
                : 'No fabric collections available yet.'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 max-w-full">
              {collections.map((collection) => (
                <FabricCollectionCard key={collection._id} collection={collection} />
              ))}
            </div>
          </>
        )}
              </>
            )}

            {collectionsSubTab === 'bodyCuts' && (
              <div className="max-w-full">
                {inspirationLoading ? (
                  <div className="flex items-center justify-center py-12 sm:py-20">
                    <div className="text-center">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-violet-500 to-purple-500 rounded-lg sm:rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 animate-pulse">
                        <Shirt size={20} className="text-white sm:w-6 sm:h-6" />
                      </div>
                      <p className="text-sm sm:text-base text-violet-600 font-medium">Loading inspiration designs...</p>
                    </div>
                  </div>
                ) : inspirationDesigns.length === 0 ? (
                  <div className="text-center py-12 sm:py-20">
                    <Shirt size={48} className="text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg">No inspiration designs yet.</p>
                    <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
                      Designs appear here only when marked public (Make it public) in Garment designs. You can also add them from Generate kit step 2.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 max-w-full">
                    {inspirationDesigns.map((design) => {
                      // Some stored image URLs can be double-encoded (e.g. %2520 -> %20),
                      // which makes the browser request a non-existent asset and triggers SPA fallback.
                      const rawImg = design.imageUrl || null;
                      const img =
                        typeof rawImg === 'string' && rawImg.includes('%25')
                          ? rawImg.replace(/%25/g, '%')
                          : rawImg;
                      return (
                        <button
                          type="button"
                          key={design._id}
                          onClick={() => setSelectedInspirationDesign(design)}
                          className="group text-left bg-white rounded-lg sm:rounded-xl border border-gray-200 hover:border-[var(--pv-primary)] hover:shadow-lg transition-all duration-300 overflow-hidden"
                        >
                          <div className="relative aspect-square bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50 flex items-center justify-center overflow-hidden">
                            {img ? (
                              <LazyImage
                                src={toThumbnailUrl(img)}
                                fallbackSrc={img}
                                alt={design.productName || 'Inspiration'}
                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                              />
                            ) : (
                              <Shirt size={32} className="text-violet-300" />
                            )}
                            <div className="absolute top-1 left-1 sm:top-2 sm:left-2 bg-violet-600 text-white text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-semibold shadow">
                              Inspiration
                            </div>
                          </div>
                          <div className="p-3">
                            <h3 className="font-semibold text-gray-800 line-clamp-2 text-sm">
                              {design.productName || 'Inspiration'}
                            </h3>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {collectionsSubTab === 'aiCreations' && (
              <div className="max-w-full">
                {kitLoading ? (
                  <div className="flex items-center justify-center py-12 sm:py-20">
                    <div className="text-center">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-fuchsia-500 to-purple-500 rounded-lg sm:rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 animate-pulse">
                        <Sparkles size={20} className="text-white sm:w-6 sm:h-6" />
                      </div>
                      <p className="text-sm sm:text-base text-fuchsia-600 font-medium">Loading generated concepts...</p>
                    </div>
                  </div>
                ) : publicKits.length === 0 ? (
                  <div className="text-center py-12 sm:py-20">
                    <Sparkles size={48} className="text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg">No generated concepts on public fabrics yet.</p>
                    <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
                      Generated designs that use this seller&apos;s public fabrics show up here.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 max-w-full">
                    {publicKits.map((kit) => {
                      const kitImage =
                        kit.generatedImageUrl || kit.thumbnail || kit.productDetails?.image || null;
                      if (!kitImage) return null;
                      return (
                        <button
                          type="button"
                          key={kit._id}
                          onClick={() => setSelectedPublicKit(kit)}
                          className="group text-left bg-white rounded-lg sm:rounded-xl border border-gray-200 hover:border-fuchsia-300 hover:shadow-lg transition-all duration-300 overflow-hidden"
                        >
                          <div className="relative aspect-[3/4] bg-gradient-to-br from-fuchsia-50 to-purple-50 overflow-hidden">
                            <img
                              src={toThumbnailUrl(kitImage)}
                              alt={kit.name || kit.productDetails?.name || 'Generated concept'}
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                              onError={(e) => {
                                if (e.currentTarget.src !== kitImage) {
                                  e.currentTarget.src = kitImage;
                                }
                              }}
                            />
                            <div className="absolute top-1 left-1 sm:top-2 sm:left-2 bg-fuchsia-600 text-white text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-semibold shadow inline-flex items-center gap-1">
                              <Sparkles size={10} className="sm:w-3 sm:h-3" />
                              AI
                            </div>
                            {kit.videoUrl && (
                              <div className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-[var(--pv-primary)] text-white p-1 rounded-full shadow">
                                <Play size={12} />
                              </div>
                            )}
                          </div>
                          <div className="p-3">
                            <h3 className="font-semibold text-gray-800 line-clamp-2 text-sm">
                              {kit.productDetails?.name || kit.name || 'Generated design'}
                            </h3>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {canLoadMoreCollections && (
              <div
                ref={observerTarget}
                className="flex justify-center items-center py-8 min-h-[100px]"
              >
                {(collectionsSubTab === 'fabrics' && isLoadingMore) ||
                (collectionsSubTab === 'bodyCuts' && inspirationLoadingMore) ||
                (collectionsSubTab === 'aiCreations' && kitLoadingMore) ? (
                  <div className="flex items-center gap-2 text-indigo-600">
                    <Loader2 size={32} className="text-indigo-500 animate-spin" />
                    <span className="text-sm font-medium">
                      {collectionsSubTab === 'fabrics' && 'Loading more collections...'}
                      {collectionsSubTab === 'bodyCuts' && 'Loading more inspirations...'}
                      {collectionsSubTab === 'aiCreations' && 'Loading more generated concepts...'}
                    </span>
                  </div>
                ) : (
                  <div className="h-20 w-full" />
                )}
              </div>
            )}
          </>
        )}
      </div>

      {selectedInspirationDesign && (
        <div
          className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4"
          onClick={() => setSelectedInspirationDesign(null)}
          role="presentation"
        >
          <div
            className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-[var(--pv-card-border)]"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="inspiration-modal-title"
          >
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between gap-2 mb-4">
                <h2 id="inspiration-modal-title" className="text-lg sm:text-xl font-bold text-gray-900 pr-2">
                  {selectedInspirationDesign.productName || 'Inspiration design'}
                </h2>
                <button
                  type="button"
                  onClick={() => setSelectedInspirationDesign(null)}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 shrink-0"
                  aria-label="Close"
                >
                  <X size={22} />
                </button>
              </div>
              {selectedInspirationDesign.imageUrl && (
                <div className="rounded-xl overflow-hidden border border-gray-100 bg-gray-50">
                  <img
                    src={selectedInspirationDesign.imageUrl}
                    alt=""
                    className="w-full h-auto object-contain max-h-[70vh]"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedPublicKit && (
        <div
          className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4"
          onClick={() => setSelectedPublicKit(null)}
          role="presentation"
        >
          <div
            className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-[var(--pv-card-border)]"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="public-kit-modal-title"
          >
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between gap-2 mb-4">
                <h2 id="public-kit-modal-title" className="text-lg sm:text-xl font-bold text-gray-900 pr-2">
                  {selectedPublicKit.productDetails?.name || selectedPublicKit.name || 'Generated concept'}
                </h2>
                <button
                  type="button"
                  onClick={() => setSelectedPublicKit(null)}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 shrink-0"
                  aria-label="Close"
                >
                  <X size={22} />
                </button>
              </div>
              {(() => {
                const out =
                  selectedPublicKit.generatedImageUrl ||
                  selectedPublicKit.thumbnail ||
                  selectedPublicKit.productDetails?.image;
                if (!out) return null;
                return (
                  <div className="mb-4 rounded-xl overflow-hidden border border-gray-100 bg-gray-50">
                    <img
                      src={out}
                      alt=""
                      className="w-full h-auto object-contain max-h-[70vh]"
                    />
                  </div>
                );
              })()}
              {selectedPublicKit.productDetails?.description && (
                <p className="text-sm text-gray-600 mb-4">{selectedPublicKit.productDetails.description}</p>
              )}
              {(() => {
                const fabricSrc =
                  selectedPublicKit.productDetails?.image || selectedPublicKit.productDetails?.originalImage;
                if (!fabricSrc || fabricSrc === selectedPublicKit.generatedImageUrl) return null;
                return (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Source fabric</p>
                    <div className="rounded-lg overflow-hidden border border-gray-200 w-24 h-24">
                      <img src={fabricSrc} alt="Source fabric" className="w-full h-full object-cover" />
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicFabricView;

